import React, { useState } from 'react';
import './ProcessPage.css';

interface StageInfo {
  id: string;
  step: number;
  title: string;
  service: string;
  model: string;
  description: string;
  inputs: string[];
  outputs: string[];
  prompt: string;
  notes?: string[];
}

const STAGES: StageInfo[] = [
  {
    id: 'preview',
    step: 0,
    title: 'Company Preview / Disambiguation',
    service: 'company_preview_service.py',
    model: 'Gemini 2.0 Flash + Google Search (primary), GPT-4o-mini (fallback)',
    description:
      'Searches the web for distinct companies matching the user\'s input name. Returns candidate companies for the user to select from when multiple entities share a similar name (e.g., "Apple" could be Apple Inc., Apple Leisure Group, etc.).',
    inputs: [
      'Company name (user input)',
      'Domain (optional)',
      'Country (optional)',
    ],
    outputs: [
      'List of CompanyCandidate objects (legal_name, country, city, website, industry, description, confidence)',
      'requires_disambiguation flag',
      'reason string',
    ],
    prompt: `Search for companies named "{company_name}" and find ALL DISTINCT entities worldwide that have this name or a very similar name.

This is for EXPORT COMPLIANCE — missing a subsidiary could mean missing a sanctioned entity.

[If domain provided: Known website domain: {domain}]
[If country provided: Focus on country: {country}]

TASK: Find up to 10 DIFFERENT companies/entities that could match this name. Be EXHAUSTIVE:
1. The main company (parent/HQ)
2. ALL regional subsidiaries (e.g., "{company_name} China", "{company_name} USA", "{company_name} Europe")
3. ALL branches and divisions that operate as separate legal entities
4. Parent company if the searched name is itself a subsidiary
5. Any joint ventures or affiliated entities with similar names

IMPORTANT: Only return REAL COMPANIES that you can verify exist through web search. Do NOT include individuals (people), and do NOT include entries from screening lists or sanctions databases — those are checked separately later in the process.

For each distinct company found, provide:
1. Legal/registered name (exact official name)
2. Country (ISO 3166-1 alpha-2 code)
3. City (headquarters location)
4. Full address (if available)
5. Website domain (without https://)
6. Industry/sector
7. Brief description (1-2 sentences)

Return JSON: { "candidates": [...], "search_notes": "..." }

RULES:
- Up to 10 candidates
- ALWAYS search for regional variants
- Each candidate = separately registered legal entity (NOT individuals)
- Confidence: HIGH (registry/website), MEDIUM (directories/news), LOW (uncertain)
- Return ONLY valid JSON`,
    notes: [
      'Disambiguation is SKIPPED if only 1 high-confidence match is found',
      'If user specifies a country and exactly 1 match exists in that country, disambiguation is skipped',
      'Uses Google Search grounding for real-time company lookup',
    ],
  },
  {
    id: 'corporate-structure',
    step: 0,
    title: 'Corporate Structure Discovery',
    service: 'corporate_structure_service.py',
    model: 'Gemini 2.0 Flash + Google Search (primary), GPT-4o-mini (fallback)',
    description:
      'After disambiguation, discovers the corporate hierarchy (parent, subsidiaries, sister companies, branches, JVs) so the user can choose which entities to include in the screening.',
    inputs: [
      'Selected company candidates from disambiguation',
    ],
    outputs: [
      'root_entity name',
      'entities[] with relationship types (SELF, PARENT, SUBSIDIARY, SISTER, BRANCH, JOINT_VENTURE)',
      'relationships[] (parent → child edges)',
      'sources[] with URLs',
    ],
    prompt: `Research the corporate structure for the following company/companies:
[company list with name, country, industry]

TASK: Build the corporate hierarchy based on VERIFIED corporate filings, annual reports, and official registries:
1. Find the ULTIMATE PARENT company
2. List known SUBSIDIARIES at every level (direct and indirect)
3. Find SISTER COMPANIES (same parent)
4. Identify BRANCHES and JOINT VENTURES
5. Search for annual reports, 10-K filings, SEC Exhibit 21, Companies House filings

IMPORTANT: Only include entities that are ACTUALLY part of this company's corporate structure based on ownership, corporate filings, or official registries. Do NOT include entities just because they have similar names.

For each entity: legal_name, relationship, country, city, industry, description, ownership_pct, confidence

Return JSON: { "root_entity": "...", "entities": [...], "relationships": [...], "sources": [...], "notes": "..." }

CRITICAL RULES:
- Up to 50 entities
- Only VERIFIED corporate relationships
- Confidence: HIGH (official filings), MEDIUM (credible sources), LOW (uncertain)
- Include queried companies with relationship "SELF"`,
    notes: [
      'User can select/deselect entities before proceeding to screening',
      'Shown as interactive tree view (desktop) or flat list (mobile)',
      'If discovery fails, screening proceeds with original selection only',
    ],
  },
  {
    id: 'enrichment',
    step: 1,
    title: 'Step 1: Company Enrichment',
    service: 'web_search_service.py → enrichment_service.py',
    model: 'Gemini 2.0 Flash + Google Search grounding',
    description:
      'Enriches the user-provided company name with verified business data (legal name, address, website, industry) from web search. This data improves CSL matching accuracy.',
    inputs: [
      'Company name',
      'Domain (optional)',
      'Country (optional)',
      'OR: selected company data from disambiguation (skips enrichment prompt)',
    ],
    outputs: [
      'EnrichedCompanyProfile dataclass: legal_name, trade_names[], website, address, city, state, postal_code, country, industry, description, parent_company',
      'enrichment_confidence (HIGH/MEDIUM/LOW)',
      'enrichment_source_urls[]',
    ],
    prompt: `Research the company "{company_name}" and provide accurate business information.

[If domain: Known website domain: {domain}]
[If country: Known country: {country}]

SEARCH FOR:
1. Official legal/registered name
2. Official website domain
3. Headquarters address (street, city, state, postal code, country)
4. "Doing business as" (DBA) or trade names
5. Brief description
6. Parent company (if subsidiary)

Return JSON: { "legal_name": "...", "trade_names": [...], "website": "...", "address": "...", "city": "...", "state": "...", "postal_code": "...", "country": "US", "industry": "...", "description": "...", "parent_company": "...", "confidence": "HIGH/MEDIUM/LOW" }

- Use ISO 3166-1 alpha-2 country codes
- Return null for fields you cannot find
- Confidence: HIGH (official sources), MEDIUM (credible sources), LOW (uncertain)`,
    notes: [
      'SKIPPED if user selected a company from disambiguation (uses that data directly)',
      'Enriched website/domain may be used later for multi-domain WHOIS checks',
      'Trade names are used to expand CSL search scope',
    ],
  },
  {
    id: 'csl-search',
    step: 2,
    title: 'Step 2: CSL Search',
    service: 'csl_service.py',
    model: 'No LLM — direct Trade.gov API call',
    description:
      'Searches the U.S. Consolidated Screening List (CSL) via the Trade.gov API. Uses enriched company data (legal name, trade names, address) for more precise matching. Applies fuzzy matching and country filtering.',
    inputs: [
      'Company name (or enriched legal name + trade names)',
      'Country (optional, for filtering)',
      'Fuzzy matching flag',
    ],
    outputs: [
      'results[]: name, source (Entity List, SDN, etc.), type, programs[], addresses[], score, name_similarity',
      'filtered_results[]: low-confidence matches separated out',
      'total count',
    ],
    prompt: 'N/A — This is a direct API call to https://api.trade.gov/consolidated_screening_list/v1/search with query parameters, not an LLM prompt.',
    notes: [
      'Enhanced search mode: searches legal_name + each trade_name separately, deduplicates',
      'Country match boosting: results matching the enriched country get higher scores',
      'Weak matches (score < threshold) are separated into filtered_results',
      'Lists searched: Entity List, SDN, Denied Persons, Military End User, Unverified List, CMIC, Foreign Sanctions Evaders, Sectoral Sanctions, and others',
    ],
  },
  {
    id: 'match-validation',
    step: 3,
    title: 'Step 3: Match Validation',
    service: 'match_validation_service.py',
    model: 'Gemini 2.0 Flash (primary), GPT-4o-mini (fallback)',
    description:
      'AI-powered analysis of each CSL match to determine if it refers to the same entity as the company being screened, or is a coincidental name overlap. Critical for reducing false positives.',
    inputs: [
      'Company name + enriched profile',
      'CSL matches (1-10 max)',
      'Domain, Country',
    ],
    outputs: [
      'validations[]: match_index, match_name, is_same_entity, relationship, confidence, reasoning',
      'summary: confirmed_count, uncertain_count, coincidental_count',
    ],
    prompt: `TASK: Analyze whether CSL matches refer to the same entity as the company being screened, or are coincidental name overlaps.

COMPANY BEING SCREENED:
- Name: {company_name}
- Domain: {domain}
- Country: {country}

ENRICHED COMPANY PROFILE (verified through web research):
- Legal Name, Address, Website, Industry, Description, Parent Company, Trade Names

CSL MATCHES TO ANALYZE:
[JSON array of match objects with name, type, source, addresses, programs, remarks, score]

For EACH match, determine:
1. is_same_entity (true/false/null)
2. relationship: EXACT | SUBSIDIARY | AFFILIATED | COINCIDENTAL | UNKNOWN
3. confidence: HIGH | MEDIUM | LOW
4. reasoning: 1-2 sentences

GUIDANCE:
- ENTITY TYPE IS CRITICAL: An individual person CANNOT be the same entity as a company → COINCIDENTAL
- ADDRESS COMPARISON IS CRITICAL: If enriched profile shows Germany but CSL match is in China → likely COINCIDENTAL
- Just sharing a country is NOT a connection
- Check for subsidiary patterns: parent name + location indicator
- Common words like "China", "International", "Trading" are not meaningful connections
- Individual persons on CSL are almost always COINCIDENTAL unless named as owner/officer

Return JSON: { "validations": [...], "summary": { "total_matches": N, "confirmed_count": N, "uncertain_count": N, "coincidental_count": N } }`,
    notes: [
      'SKIPPED if >10 CSL matches (too many for reliable AI analysis)',
      'When skipped, all matches are treated as confirmed → HIGH risk',
      'COINCIDENTAL matches are filtered out before being passed to AI Research (Step 4)',
      'Relationship types: EXACT (same entity), SUBSIDIARY (parent/child), AFFILIATED (business relationship), COINCIDENTAL (name overlap, different entity), UNKNOWN (cannot determine)',
    ],
  },
  {
    id: 'ai-research',
    step: 4,
    title: 'Step 4: AI Research',
    service: 'ai_service.py',
    model: 'Gemini 2.0 Flash + Google Search grounding (primary), GPT-4o-mini (fallback)',
    description:
      'Comprehensive AI-powered research on the company for export compliance risks. Investigates screening list matches (excluding coincidental ones), PRC connections, military ties, state ownership, and other risk indicators.',
    inputs: [
      'Company name',
      'Domain, Country',
      'CSL matches (with coincidental matches filtered out)',
      'Additional names (trade names from enrichment)',
      'China connection detection flag',
    ],
    outputs: [
      'company_overview',
      'public_sources_confirm[], public_sources_do_not_confirm[]',
      'screening_list_analysis[] (per-list: is_same_entity, listing_reason, restrictions, federal_register_url)',
      'prc_verification (if China-connected): mainland_registration, hong_kong_entity, state_ownership, military_connections',
      'risk_indicators[]',
      'key_sources[] with URLs',
      'grounding_source_urls[] (from Google Search)',
      'research_confidence',
    ],
    prompt: `You are an export compliance research assistant conducting due diligence on a company.

Company Name: {company_name}
Website Domain: {domain}
Country: {country}
Related entities also being screened: {additional_names}

=== SCREENING LIST MATCHES ===
[Only non-coincidental matches included]
For each: name, source, programs, countries
→ For matches that appear to be the SAME entity, explain WHY they were added to the list

Return JSON with:
- company_overview
- public_sources_confirm[] (specific FACTS confirmed by public sources)
- public_sources_do_not_confirm[] (what remains unclear)
- screening_list_analysis[] (per-list: list_name, is_same_entity, confidence, reasoning, listing_reason, listing_date, restrictions, federal_register_url)
- prc_verification (if China-connected): mainland_registration, hong_kong_entity, state_ownership, military_connections
- risk_indicators[] (specific risk indicators, NOT routine compliance matters)
- research_confidence
- key_sources[]

GUIDELINES:
- Be INVESTIGATIVE — specific facts, registration numbers, dates
- Search for official listing reasons (Federal Register, BIS press releases)
- Distinguish confirmed vs not-yet-confirmed
- HK registration ≠ Mainland PRC registration
- Cite specific sources`,
    notes: [
      'China connection auto-detected from: company name, domain (.cn/.hk), country code (CN/HK/MO/TW), CSL match addresses',
      'PRC verification section only included when China connection is detected',
      'COINCIDENTAL CSL matches are excluded before this step to prevent false risk indicators',
      'Google Search grounding provides real-time source URLs',
    ],
  },
  {
    id: 'domain-check',
    step: 5,
    title: 'Step 5: Multi-Domain Check',
    service: 'domain_service.py',
    model: 'No LLM — IP2WHOIS API',
    description:
      'Checks WHOIS registration data for all discovered domains. Domains are collected from: user input, enrichment profile website, and domains mentioned in AI research text. Up to 5 domains checked.',
    inputs: [
      'User-provided domain',
      'Enriched profile website',
      'AI research text (domain extraction via regex)',
    ],
    outputs: [
      'Per domain: success, domain, create_date, domain_age_days, registrar, registrant_country, risk_flags[], is_primary',
      'Backward-compatible top-level fields from primary domain',
      'total_checked count',
    ],
    prompt: 'N/A — Direct API call to IP2WHOIS (https://api.ip2whois.com/v2). Domain extraction from AI text uses regex pattern matching with false-positive filtering (excludes file extensions like .pdf, .doc, etc.).',
    notes: [
      'Runs AFTER AI research so we can extract domains mentioned in research text',
      'Risk flags: "Domain less than 1 year old", "Domain less than 6 months old - HIGH RISK"',
      'Capped at 5 domains to manage API rate limits (~15s max)',
      'Primary domain = user-provided domain (or first discovered domain)',
    ],
  },
  {
    id: 'risk-assessment',
    step: 6,
    title: 'Step 6: Risk Assessment',
    service: 'tasks.py → compile_risk_assessment()',
    model: 'No LLM — rule-based logic',
    description:
      'Compiles a comprehensive risk assessment from all gathered data using deterministic rules. Evaluates CSL matches, AI research findings, domain data, and OpenSanctions results.',
    inputs: [
      'All results from Steps 1-5',
    ],
    outputs: [
      'risk_level: LOW | MEDIUM | HIGH',
      'risk_factors[]: {text, severity}',
      'recommendation string',
      'source_risks: per-source risk level + summary',
      'report_id, screening_timestamp',
    ],
    prompt: `N/A — Deterministic rule-based assessment:

CSL Risk Rules:
- Validation skipped (>10 matches) → HIGH
- Any CONFIRMED matches (EXACT/SUBSIDIARY/AFFILIATED) → HIGH
- Any UNCERTAIN matches → MEDIUM
- All COINCIDENTAL → LOW (INFO severity)
- No matches → LOW

Domain Risk Rules:
- Domain < 6 months old → HIGH
- Domain < 1 year old → MEDIUM
- No risk flags → LOW

AI Research Risk Rules:
- Risk indicators matching red-flag keywords (sanctioned, denied, military, etc.) → MEDIUM
- Routine compliance keywords are filtered out to avoid false positives
- State-Owned Enterprise detected → HIGH
- Military connections detected → HIGH

Final risk = highest of all source risks

Recommendation is dynamically generated based on specific triggers:
- csl_confirmed → "BIS license may be required..."
- soe/military → "Enhanced end-use screening required per EAR 744.21..."
- opensanctions_hit → "Check OFAC compliance requirements..."`,
    notes: [
      'Rule-based, not AI — deterministic and auditable',
      'Red-flag keywords include: entity list, denied, sanctioned, ofac, sdn, blocked, prohibited, violation, proliferation, wmd, nuclear, etc.',
      'Routine keywords are excluded: subject to ear, standard compliance, general license, compliant with, etc.',
    ],
  },
  {
    id: 'executive-brief',
    step: 7,
    title: 'Step 7: Executive Brief',
    service: 'ai_service.py → generate_executive_brief()',
    model: 'Gemini 2.0 Flash + Google Search (primary), GPT-4o-mini (fallback)',
    description:
      'Generates a narrative executive summary from all screening results. Written in professional compliance-report prose with inline citations.',
    inputs: [
      'All screening results (enrichment, CSL, validation, AI research, domain, risk assessment, OpenSanctions)',
      'Available source URLs from grounding',
    ],
    outputs: [
      'narrative: 2-3 paragraph executive summary',
      'cited_sources[]: {label, url} deep links to specific documents',
    ],
    prompt: `You are a senior export compliance analyst writing an executive brief for a screening report.

=== COMPANY ===
Name, Country, Industry, Description, AI Overview

=== RISK ASSESSMENT ===
Overall Risk Level, Risk Factors with severity, Recommendation

=== CSL MATCHES ===
[Top 5 matches with name and source]

=== SCREENING LIST ANALYSIS ===
[Per-list: same entity determination, listing reason, date]

=== RISK INDICATORS ===
[Specific risk indicators from AI research]

=== PRC VERIFICATION (if applicable) ===
SOE status, Military connections

=== AVAILABLE SOURCE URLs ===
[Deep links from Google Search grounding]

YOUR TASK:
Write JSON: { "narrative": "2-3 paragraphs...", "cited_sources": [...] }

Paragraph 1: Who the company is and what they do
Paragraph 2: Why they are HIGH/MEDIUM/LOW risk with specific citations
Paragraph 3: What this means for the transaction and next steps

RULES:
- Professional compliance-report prose, NOT bullet points
- Reference specific list names, dates, legal citations inline
- cited_sources MUST be deep links, NOT front pages
- If LOW risk, keep concise — confirm no concerns`,
    notes: [
      'This is the last AI-powered step before the report is finalized',
      'Narrative appears at the top of both web and PDF reports',
      'If generation fails, web UI falls back to company_overview + key findings bullets',
    ],
  },
];

const ProcessPage: React.FC = () => {
  const [expandedStage, setExpandedStage] = useState<string | null>(null);

  const toggleStage = (id: string) => {
    setExpandedStage(prev => (prev === id ? null : id));
  };

  const expandAll = () => {
    setExpandedStage('__all__');
  };

  const collapseAll = () => {
    setExpandedStage(null);
  };

  const isExpanded = (id: string) =>
    expandedStage === '__all__' || expandedStage === id;

  // Separate pre-screening steps from pipeline steps
  const preSteps = STAGES.filter(s => s.step === 0);
  const pipelineSteps = STAGES.filter(s => s.step > 0);

  return (
    <div className="process-page">
      <div className="process-header">
        <h1>Screening Process &amp; Prompts</h1>
        <p className="process-subtitle">
          Complete documentation of every stage in the CompanySieve screening
          pipeline, including the exact AI prompts, inputs, outputs, and
          decision logic.
        </p>
        <div className="process-actions">
          <button className="process-btn" onClick={expandAll}>
            Expand All
          </button>
          <button className="process-btn" onClick={collapseAll}>
            Collapse All
          </button>
        </div>
      </div>

      {/* Flow diagram */}
      <div className="process-flow">
        <div className="flow-label">Screening Pipeline</div>
        <div className="flow-steps">
          {['Input', 'Preview', 'Structure', 'Enrich', 'CSL', 'Validate', 'AI Research', 'Domain', 'Risk', 'Brief', 'Report'].map(
            (label, i) => (
              <React.Fragment key={label}>
                <span className="flow-node">{label}</span>
                {i < 10 && <span className="flow-arrow">&rarr;</span>}
              </React.Fragment>
            )
          )}
        </div>
      </div>

      {/* Pre-screening stages */}
      <h2 className="process-group-title">Pre-Screening (User Interaction)</h2>
      <p className="process-group-desc">
        These steps happen before the comprehensive check begins. The user
        selects which company/entities to screen.
      </p>

      {preSteps.map(stage => (
        <StageCard
          key={stage.id}
          stage={stage}
          isExpanded={isExpanded(stage.id)}
          onToggle={() => toggleStage(stage.id)}
        />
      ))}

      {/* Pipeline stages */}
      <h2 className="process-group-title">
        Screening Pipeline (Automated — Celery Task)
      </h2>
      <p className="process-group-desc">
        These steps run as a single Celery background task. The frontend polls
        for progress every 2 seconds.
      </p>

      {pipelineSteps.map(stage => (
        <StageCard
          key={stage.id}
          stage={stage}
          isExpanded={isExpanded(stage.id)}
          onToggle={() => toggleStage(stage.id)}
        />
      ))}

      {/* After screening */}
      <h2 className="process-group-title">Post-Screening (On-Demand)</h2>
      <div className="process-post-screening">
        <div className="post-item">
          <h4>OpenSanctions Check</h4>
          <p>
            Optional on-demand screening against international sanctions lists
            (EU, UK, UN). Triggered by the user clicking "Run OpenSanctions
            Check" after reviewing CSL results. Uses the OpenSanctions API
            with up to 10 entity names.
          </p>
        </div>
        <div className="post-item">
          <h4>PDF Report Generation</h4>
          <p>
            Generates a professional legal memorandum PDF using ReportLab.
            Includes all screening data: executive brief, subject
            identification, screened entities, CSL results, AI research,
            domain intelligence, OpenSanctions, and methodology.
          </p>
        </div>
      </div>
    </div>
  );
};

/* ---- Stage Card Component ---- */

interface StageCardProps {
  stage: StageInfo;
  isExpanded: boolean;
  onToggle: () => void;
}

const StageCard: React.FC<StageCardProps> = ({ stage, isExpanded, onToggle }) => (
  <div className={`stage-card ${isExpanded ? 'expanded' : ''}`}>
    <div
      className="stage-card-header"
      onClick={onToggle}
      role="button"
      tabIndex={0}
      onKeyDown={e => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onToggle();
        }
      }}
    >
      <span className="stage-expand-icon">
        {isExpanded ? '\u25BE' : '\u25B8'}
      </span>
      <h3 className="stage-title">{stage.title}</h3>
      <span className="stage-model-badge">{stage.model.split(' (')[0]}</span>
    </div>

    {isExpanded && (
      <div className="stage-card-body">
        <p className="stage-description">{stage.description}</p>

        <div className="stage-meta">
          <div className="stage-meta-item">
            <strong>Service File:</strong>{' '}
            <code>{stage.service}</code>
          </div>
          <div className="stage-meta-item">
            <strong>AI Model:</strong> {stage.model}
          </div>
        </div>

        <div className="stage-io">
          <div className="stage-io-col">
            <h4>Inputs</h4>
            <ul>
              {stage.inputs.map((input, i) => (
                <li key={i}>{input}</li>
              ))}
            </ul>
          </div>
          <div className="stage-io-col">
            <h4>Outputs</h4>
            <ul>
              {stage.outputs.map((output, i) => (
                <li key={i}>{output}</li>
              ))}
            </ul>
          </div>
        </div>

        <div className="stage-prompt-section">
          <h4>Prompt / Logic</h4>
          <pre className="stage-prompt">{stage.prompt}</pre>
        </div>

        {stage.notes && stage.notes.length > 0 && (
          <div className="stage-notes">
            <h4>Notes</h4>
            <ul>
              {stage.notes.map((note, i) => (
                <li key={i}>{note}</li>
              ))}
            </ul>
          </div>
        )}
      </div>
    )}
  </div>
);

export default ProcessPage;
